import type { ComputedRef, Ref } from 'vue';
import { cloneDeep, size } from 'lodash-es';
import {
  computed,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { Permission } from '@modules/permissions/utils';
import type { IGroupSelectValues } from '@modules/reports/components/VGroupByParams/types';
import type { IGroupSortableSettings } from '@modules/reports/components/VGroupBySortable/types';
import type { IGroupSortableOptions, IReportOptionsResponse } from '@modules/reports/types';
import type { ISmartFilterColumnDataDate } from '@modules/smart-filters/types';
import type {
  ITableColumnsOptions,
  ITableDataResponse,
  ITableMultiselectActions,
  ITableState,
  ITableStateUpdateAction,
} from '@modules/tables/types';
import { useDownloadReport } from '@modules/download-report/composables';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { PLAYERS_PERMISSIONS } from '@modules/players/constants';
import { ADMIN_REPORTS_PLAYER_PROFILE, ADMIN_REPORTS_ROUTE_NAME } from '@modules/reports/constants';
import { ReportType } from '@modules/reports/enums';
import { useReportDownloadMutation } from '@modules/reports/mutations';
import { useReportGenerateQuery, useReportOptionsQuery } from '@modules/reports/queries';
import {
  EGroupNames,
} from '@modules/reports/types';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { useTableData } from '@modules/tables/composables';
import { versionSplitterTags } from '@modules/tables/utils/formatters';
import {
  assert,
  isArray,
  isDate,
  isString,
} from '@utils/guards';
import { formatDate } from '@vueuse/core';

import type { IReportPayload } from '../types';

export interface ReportComposable {
  reportPayload: Ref<IReportPayload>;
  tableState: Ref<ITableState>;
  tableData: Ref<ITableDataResponse<Record<string, string | number>> | undefined>;
  isTableLoading: Ref<boolean>;
  isTableFetching: Ref<boolean>;
  isReportLoading: Ref<boolean>;
  options: Ref<IReportOptionsResponse | undefined>;
  isOptionsLoading: Ref<boolean>;
  reportTableVisible: Ref<boolean>;
  isSortableGrouping: ComputedRef<boolean>;
  groupByValues: ComputedRef<IGroupSelectValues>;
  groupBySortableValues: ComputedRef<IGroupSortableOptions>;
  groupBySortableSettings: ComputedRef<IGroupSortableSettings>;
  availableGroupByNames: ComputedRef<EGroupNames[]>;
  selectedRows: Ref<number[] | undefined>;
  rowsPerPage: Ref<number>;
  columnOptions: ITableColumnsOptions;
  tableActions: Ref<ITableMultiselectActions>;
  subtotalEnabled: Ref<boolean>;
  generateReport(): Promise<void>;
  updateTableState(state: Partial<ITableState>, action?: ITableStateUpdateAction): void;
  onDownloadReport(): Promise<void>;
  onSelectSegment(id: number): void;
  onSelectStructure(id: number): void;
  onPeriodChange(data: ISmartFilterColumnDataDate): void;
  onGroupParamsChange(key: keyof IGroupSelectValues, value: string | string[]): void;
  onTableActionItemSelect(optionId: string, listItem?: Record<string, string | number>): void;
  toggleSubtotalDisplay(): void;
}

export default function useReport(
  reportType: Ref<ReportType>,
  generateReportPermission: Ref<Permission>,
  downloadReportPermission: Ref<Permission>,
  isStructuresAllowed: Ref<boolean>,
  translationKey: Ref<symbol>,
): ReportComposable {
  const route = useRoute();
  const router = useRouter();
  const { hasPermission } = usePermissionsStore();
  const { getTranslation } = useI18n();

  const isSortableGrouping = computed<boolean>(() => reportType.value === ReportType.REPORTS || reportType.value === ReportType.CREATIVE_REPORTS);

  const reportPayload = ref<IReportPayload>({
    period: {
      start: null,
      end: null,
    },
    group_by_date: 'group_by_alltime',
    group_by_affiliate_params: isSortableGrouping.value ? ['group_by_merchant'] : 'group_by_merchant',
    group_by_player_params: 'all_players',
    per_page: 10,
    segment_id: 0,
  });
  const reportPayloadForQuery = ref<IReportPayload>();
  const reportTableVisible = ref(false);
  const selectedRows = ref<number[]>();
  const rowsPerPage = ref<number>(10);
  const subtotalEnabled = ref(true);

  const availableGroupByNames = computed<EGroupNames[]>(() => (isSortableGrouping.value
    ? [
        EGroupNames.DATE,
        EGroupNames.PLAYER,
        EGroupNames.ROW_ON_PAGE,
      ]
    : [
        EGroupNames.DATE,
        EGroupNames.AFFILIATE,
        EGroupNames.PLAYER,
        EGroupNames.ROW_ON_PAGE,
      ]
  ));

  const groupByValues = computed<IGroupSelectValues>(() => ({
    [EGroupNames.DATE]: reportPayload.value.group_by_date,
    [EGroupNames.AFFILIATE]: isArray(reportPayload.value.group_by_affiliate_params)
      ? reportPayload.value.group_by_affiliate_params[0]
      : reportPayload.value.group_by_affiliate_params,
    [EGroupNames.PLAYER]: reportPayload.value.group_by_player_params,
    [EGroupNames.ROW_ON_PAGE]: String(reportPayload.value.per_page),
  }));

  const groupBySortableValues = computed<IGroupSortableOptions>(() => ({
    [EGroupNames.AFFILIATE]: isArray(reportPayload.value.group_by_affiliate_params) ? reportPayload.value.group_by_affiliate_params : [],
  }));

  const groupBySortableSettings = computed<IGroupSortableSettings>(() => ({
    [EGroupNames.AFFILIATE]: {
      label: getTranslation(translationKey.value, 'REPORTS.GROUP_BY.AFFILIATE.LABEL').value,
      required: true,
    },
  }));

  watch(isStructuresAllowed, (val) => {
    reportPayload.value.structure_id = val ? 0 : undefined;
  });

  const { data: options, isFetching: isOptionsLoading } = useReportOptionsQuery(reportType, generateReportPermission);

  const {
    tableState,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
    data: tableData,
    updateTableState: updateState,
    refetch,
  } = useTableData(
    useReportGenerateQuery,
    reportType,
    generateReportPermission,
    reportPayloadForQuery,
  );

  const {
    isReportLoading,
    onDownloadReport,
  } = useDownloadReport(
    useReportDownloadMutation(reportType, downloadReportPermission, reportPayload),
    tableState,
  );

  watch(tableData, () => {
    rowsPerPage.value = reportPayload.value.per_page;
  });

  const columnOptions: ITableColumnsOptions = {
    transaction_date_for_reports: {
      defaultMobilePreview: true,
    },
    condition_version: {
      formatValue: versionSplitterTags,
    },
  };

  const isPlayerProfileAvailable = computed(() => size(
    tableData.value?.columns_for_display_data?.filter(
      (column) => ['cid', 'merchant_id'].includes(column.name),
    ),
  ) === 2 && hasPermission(PLAYERS_PERMISSIONS.PLAYERS_SHOW).value);

  const tableActions = computed<ITableMultiselectActions>(() => {
    const actions: ITableMultiselectActions = {};

    if (isPlayerProfileAvailable.value) {
      actions.changePlayer = {
        label: getTranslation(translationKey.value, 'REPORTS.TABLE_ACTIONS.CHANGE_PLAYER').value,
        icon: 'pi-user-edit',
      };
    }

    return actions;
  });

  function onSelectSegment(id: number): void {
    reportPayload.value.segment_id = id;
  }

  function onSelectStructure(id: number): void {
    reportPayload.value.structure_id = id;
  }

  function onPeriodChange({ start, end }: ISmartFilterColumnDataDate): void {
    if (isDate(start) && isDate(end)) {
      reportPayload.value.period = {
        start: formatDate(start, RESPONSE_DATE_FORMAT),
        end: formatDate(end, RESPONSE_DATE_FORMAT),
      };
    }
  }

  function onGroupParamsChange(key: EGroupNames, value: string | string[]): void {
    switch (key) {
      case EGroupNames.DATE:
        assert(isString(value));
        reportPayload.value.group_by_date = value;
        break;
      case EGroupNames.AFFILIATE:
        reportPayload.value.group_by_affiliate_params = value;
        break;
      case EGroupNames.PLAYER:
        assert(isString(value));
        reportPayload.value.group_by_player_params = value;
        break;
      case EGroupNames.ROW_ON_PAGE:
        assert(isString(value));
        reportPayload.value.per_page = Number(value);
        break;
      default:
        break;
    }
  }

  async function generateReport(): Promise<void> {
    // Refresh route in case player profile modal is opened
    if (route.matched.some((val) => val.name === ADMIN_REPORTS_PLAYER_PROFILE)) {
      await router.replace({ name: ADMIN_REPORTS_ROUTE_NAME, query: route.query });
    }

    reportTableVisible.value = true;
    updateState({ page: 1 });
    reportPayloadForQuery.value = cloneDeep(reportPayload.value);

    if (isTableFetching.value) {
      return;
    }

    selectedRows.value = undefined;
    await refetch();
    selectedRows.value = [];
  }

  function updateTableState(state: Partial<ITableState>, action?: ITableStateUpdateAction): void {
    updateState(state, action);
    void refetch();
  }

  function onTableActionItemSelect(optionId: string, listItem?: Record<string, string | number>): void {
    if (optionId === 'changePlayer') {
      void router.push({ name: ADMIN_REPORTS_PLAYER_PROFILE, params: { id: listItem?.cid, merchant: listItem?.merchant_id } });
    }
  }

  function toggleSubtotalDisplay(): void {
    subtotalEnabled.value = !subtotalEnabled.value;
  }

  return {
    reportPayload,
    tableState,
    tableData,
    isTableLoading,
    isTableFetching,
    isReportLoading,
    options,
    isOptionsLoading,
    reportTableVisible,
    isSortableGrouping,
    groupByValues,
    groupBySortableValues,
    groupBySortableSettings,
    availableGroupByNames,
    selectedRows,
    rowsPerPage,
    columnOptions,
    tableActions,
    subtotalEnabled,
    generateReport,
    updateTableState,
    onDownloadReport,
    onSelectSegment,
    onSelectStructure,
    onPeriodChange,
    onGroupParamsChange,
    onTableActionItemSelect,
    toggleSubtotalDisplay,
  };
}
